import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { supabase } from "../lib/supabase";
import { localDayKey } from "../lib/days";
import { numberText, seriesPoints, type ChartEntry } from "../lib/progressChart";
import { ruleBodyIsLoad, type OneRMRule, type WeighIn } from "../lib/oneRepMax";
import {
  snappedTestedMax, testedMaxStanding, TESTED_MAX_BODY_CEILING_KG, TESTED_MAX_CEILING_KG, TESTED_MAX_STEP, type TestedMaxEntry,
} from "../lib/testedMax";
import { entryFromRow, estimateDays, insertRow, liftedOnValid, startKg, testedMaxLine, typedKg } from "../lib/testedMaxView";

/* 🔴 W125 part 2 (b) — THE TESTED MAX UNDER THE COACH'S 1RM CHART, the phone's `TestedMaxBar` on the web: one line
 * (`Tested 100 kg · Wed 12 Aug`, or `Tested max` with nothing typed) and one button, Add / Edit; the editor is one big
 * number over a 0.5 kg tick ruler, `Lifted on` (never after today), Save, and Clear when a max exists. Drawn only for a lift
 * whose coach turned the 1RM on. The rule is `lib/testedMax.ts` (GENERATED from `TestedMax.swift`).
 * THE ROWS: `public.tested_maxes`, append-only — Save is a NEW row (the latest is current), Clear deletes the client + lift's
 * rows; the coach names `client_id` (the policy holds it to a client they ACTIVELY coach) and never `entered_by` (the
 * server's `auth.uid()`). Rehearsed per identity 21/21 (`tools/tested-max-20261024/`).
 * THE WEB DEPARTURES, named: the editor opens IN PLACE under the line (the phone's is a sheet), and the big number is also a
 * field — a coach at a laptop types `102.5` rather than drags a ruler; the ruler follows the wheel and the arrow keys
 * (← → 0.5 kg, Page Up/Down 5 kg). */

const TICK_PX = 10;

export function TestedMaxBar({ clientId, exerciseId, rule, entries, weighIns, now }: {
  clientId: string; exerciseId: string; rule: OneRMRule; entries: readonly ChartEntry[]; weighIns: readonly WeighIn[]; now: number;
}) {
  const body = ruleBodyIsLoad(rule);
  const [entry, setEntry] = useState<TestedMaxEntry | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState("loading"); setEditing(false);
    void (async () => {
      const { data, error } = await supabase.from("tested_maxes").select("max_kg, tested_on")
        .eq("client_id", clientId).eq("coach_exercise_id", exerciseId)
        .order("created_at", { ascending: false }).order("id", { ascending: false }).limit(1);
      if (cancelled) return;
      if (error !== null) { setState("failed"); return; }
      setEntry(entryFromRow(data?.[0])); setState("ready");
    })();
    return () => { cancelled = true; };
  }, [clientId, exerciseId]);

  const today = localDayKey(new Date(now));
  const standing = entry === null ? "current" : testedMaxStanding(entry, estimateDays(seriesPoints("oneRM", entries, rule, weighIns)), today);
  const line = state === "failed" ? "Tested max · not loaded" : testedMaxLine(entry, standing, body);

  const save = async (e: TestedMaxEntry): Promise<boolean> => {
    const { error } = await supabase.from("tested_maxes").insert(insertRow(clientId, exerciseId, e));
    if (error !== null) return false;
    setEntry(e); setEditing(false); return true;
  };
  const clear = async (): Promise<boolean> => {
    const { error } = await supabase.from("tested_maxes").delete().eq("client_id", clientId).eq("coach_exercise_id", exerciseId);
    if (error !== null) return false;
    setEntry(null); setEditing(false); return true;
  };

  return (
    <div className="tmax">
      <div className="tmax-row">
        <span className={`tmax-line${entry === null ? " is-empty" : ""}`}>{line}</span>
        {state === "ready" && !editing && (
          <button type="button" className="tmax-open" aria-label={entry === null ? "Add tested max" : "Edit tested max"}
                  onClick={() => setEditing(true)}>{entry === null ? "Add" : "Edit"}</button>
        )}
      </div>
      {editing && (
        <TestedMaxEditor start={entry} bodyIsLoad={body} today={today} onSave={save}
                         onClear={entry === null ? null : clear} onCancel={() => setEditing(false)} />
      )}
    </div>
  );
}

function TestedMaxEditor({ start, bodyIsLoad, today, onSave, onClear, onCancel }: {
  start: TestedMaxEntry | null; bodyIsLoad: boolean; today: string;
  onSave: (e: TestedMaxEntry) => Promise<boolean>; onClear: (() => Promise<boolean>) | null; onCancel: () => void;
}) {
  const [kg, setKg] = useState(() => startKg(start, bodyIsLoad));
  const [text, setText] = useState(() => numberText(startKg(start, bodyIsLoad)));
  const [day, setDay] = useState(start?.testedOn ?? today);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const dayId = useId(), kgId = useId();
  const dayOk = liftedOnValid(day, today);

  const set = (v: number) => { setKg(v); setText(numberText(v)); };
  const commitText = () => { const v = typedKg(text, bodyIsLoad); if (v === null) setText(numberText(kg)); else set(v); };
  const run = async (act: () => Promise<boolean>) => {
    setBusy(true); setFailed(false);
    const ok = await act();
    setBusy(false); if (!ok) setFailed(true);
  };

  return (
    <div className="tmax-editor" role="group" aria-label="Tested max">
      <div className="tmax-figure">
        {bodyIsLoad && <span className="tmax-sign" aria-hidden="true">+</span>}
        <input id={kgId} className="tmax-value" inputMode="decimal" value={text} aria-label="Tested max in kilograms"
               style={{ width: `${Math.max(2, text.length) + 0.5}ch` }}
               onChange={(e) => { setText(e.target.value); const v = typedKg(e.target.value, bodyIsLoad); if (v !== null) setKg(v); }}
               onBlur={commitText}
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitText(); } }} />
        <span className="tmax-unit">kg</span>
      </div>
      <KgRuler kg={kg} ceiling={bodyIsLoad ? TESTED_MAX_BODY_CEILING_KG : TESTED_MAX_CEILING_KG}
               onChange={(v) => set(snappedTestedMax(v, bodyIsLoad))} />
      <label className="tmax-day" htmlFor={dayId}>
        <span>Lifted on</span>
        <input id={dayId} type="date" className="ht-input" value={day} max={today} onChange={(e) => setDay(e.target.value)} />
      </label>
      {failed && <p className="tmax-failed" role="alert">Not saved. Try again.</p>}
      <div className="tmax-actions">
        {onClear !== null && (
          <button type="button" className="ht-button ht-button-quiet tmax-clear" disabled={busy}
                  onClick={() => { void run(onClear); }}>Clear tested max</button>
        )}
        <span className="tmax-gap" />
        <button type="button" className="ht-button" onClick={onCancel}>Cancel</button>
        <button type="button" className="ht-button ht-button-primary" disabled={busy || !dayOk}
                onClick={() => { void run(() => onSave({ kg, testedOn: day })); }}>{busy ? "Saving…" : "Save"}</button>
      </div>
    </div>
  );
}

/** One big number's horizontal tick ruler — a tick per 0.5 kg, a taller one per 5, a figure per 10; the centre line is the
 *  value and the strip snaps to a tick. A slider to assistive technology, one step per arrow. */
function KgRuler({ kg, ceiling, onChange }: { kg: number; ceiling: number; onChange: (kg: number) => void }) {
  const strip = useRef<HTMLDivElement>(null);
  const count = Math.round(ceiling / TESTED_MAX_STEP);
  const index = Math.round(kg / TESTED_MAX_STEP);
  const fromScroll = useRef(false);

  // The value moved from outside (typed, a key, the first paint): bring its tick under the centre line.
  useEffect(() => {
    const el = strip.current;
    if (el === null) return;
    if (fromScroll.current) { fromScroll.current = false; return; }
    el.scrollLeft = index * TICK_PX;
  }, [index]);
  // A vertical wheel moves the strip sideways (a laptop mouse has no sideways wheel); passive: false so the page stays put.
  useEffect(() => {
    const el = strip.current;
    if (el === null) return;
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", wheel, { passive: false });
    return () => el.removeEventListener("wheel", wheel);
  }, []);

  const step = (n: number) => onChange(Math.min(count, Math.max(0, index + n)) * TESTED_MAX_STEP);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const by: Record<string, number> = { ArrowRight: 1, ArrowUp: 1, ArrowLeft: -1, ArrowDown: -1, PageUp: 10, PageDown: -10, Home: -count, End: count };
    const n = by[e.key];
    if (n === undefined) return;
    e.preventDefault();
    step(n);
  };

  return (
    <div className="tmax-ruler" role="slider" tabIndex={0} aria-label="Tested max" aria-valuemin={0} aria-valuemax={ceiling}
         aria-valuenow={kg} aria-valuetext={`${numberText(kg)} kilograms`} onKeyDown={onKey}>
      <div className="tmax-strip" ref={strip}
           onScroll={(e) => {
             const i = Math.min(count, Math.max(0, Math.round(e.currentTarget.scrollLeft / TICK_PX)));
             if (i !== index) { fromScroll.current = true; onChange(i * TESTED_MAX_STEP); }
           }}>
        <div className="tmax-ticks" aria-hidden="true">
          {Array.from({ length: count + 1 }, (_, i) => (
            <span key={i} className={`tmax-tick${i % 20 === 0 ? " is-ten" : i % 10 === 0 ? " is-five" : ""}`}>
              {i % 20 === 0 && <span className="tmax-tick-label">{i * TESTED_MAX_STEP}</span>}
            </span>
          ))}
        </div>
      </div>
      <span className="tmax-centre" aria-hidden="true" />
    </div>
  );
}
